import React, { useCallback, useRef, useState } from 'react'
import { Tooltip } from '@mui/material'
import { Handyman, PowerSettingsNew } from '@mui/icons-material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import { segmentBox } from '../model/timeGrid'

const SEGMENT_TOP = 6
const SEGMENT_HEIGHT = 22
// The presence is drawn thinner than the clock-ins, so the two rows of a worker do not look alike (PO 2026-10-17).
const PRESENCE_TOP = 10
const PRESENCE_HEIGHT = 14
// Parallel clock-ins (RN29) each keep a readable row and the lane grows to hold them, instead of slicing the 22 px of one row:
// five parallel clock-ins were five red lines of 3 px nobody could read (PO 2026-10-17, test database).
const TRACK_HEIGHT = 34
const SUB_ROW_HEIGHT = 18
const SUB_ROW_GAP = 2
/** The height of the track of a lane: the usual one, or the one its widest group of parallel clock-ins needs. */
const trackHeightOf = (segments) => {
  const rows = segments.reduce((most, segment) => Math.max(most, segment.subRows || 1), 1)
  return rows > 1 ? Math.max(TRACK_HEIGHT, 2 * SEGMENT_TOP + rows * SUB_ROW_HEIGHT + (rows - 1) * SUB_ROW_GAP) : TRACK_HEIGHT
}
const NO_ROWS = []

// The two rows of a worker (PO 2026-10-17): the name once, on the presence row, and each row saying what it is, with an icon and
// an explanation on hover. The other lanes keep «**name** sub».
const WORKER_ROWS = {
  presence: { Icon: PowerSettingsNew, caption: 'Connected to the machine', hint: 'Presence: from when the worker logs in on the machine until they log out.' },
  imputations: { Icon: Handyman, caption: 'Clocked in on jobs', hint: 'Clock-ins: the time the worker charges to each job; «stopped» while they are connected without a job.' }
}

// One lane of a block: the sticky label («**name** sub», or what a row of a worker is) and the track with its segments (maqueta `.lane`). Memoised: while
// the user scrolls, zooms, hovers or drags, only the lanes whose props changed are painted again (RN36). The segments are plain
// elements; the tooltip and the selection are shared and found by delegation through `data-seg` (T-3).
//
// Keyboard: the lane is ONE tab stop (roving tabindex). The segment that has the focus, or the first one, is tabbable; ← and →
// move the focus along the lane. Focusing a segment still shows the shared tooltip (S31).
//
// `overlay` is only given to the lanes the selected correction touches: `band` (the hatched stretch, RN41) and, in the
// clock-ins lane of the worker, the `handles` to drag (RN40): `[{ kind, valueMs }]`, moved with `onDrag(kind, ms)` or the keyboard
// `onNudge(kind, direction, big)`.
const TimelineLane = ({ blockKey, lane, fromMs, pxPerMs, trackWidth, alternate, highlightedRowIds = NO_ROWS, overlay, onDrag, onNudge }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  const [activeKey, setActiveKey] = useState('')
  const trackRef = useRef(null)
  const dragging = useRef('')
  const tabbableKey = lane.segments.some((segment) => segment.key === activeKey) ? activeKey : (lane.segments[0] ? lane.segments[0].key : '')

  const handleFocus = useCallback((event) => {
    const target = event.target.closest ? event.target.closest('[data-seg]') : null
    if (target) setActiveKey(target.getAttribute('data-seg').split('|')[2])
  }, [])

  const handleKeyDown = useCallback((event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    const handle = event.target.closest ? event.target.closest('[data-handle]') : null
    if (handle) { // a handle is a slider: ← and → move it 5 min, with Shift 60 min (RN40)
      event.preventDefault()
      event.stopPropagation()
      onNudge(handle.getAttribute('data-handle'), event.key === 'ArrowLeft' ? -1 : 1, event.shiftKey)
      return
    }
    const target = event.target.closest ? event.target.closest('[data-seg]') : null
    if (!target) return
    const siblings = Array.from(event.currentTarget.querySelectorAll('[data-seg]'))
    const next = siblings[siblings.indexOf(target) + (event.key === 'ArrowRight' ? 1 : -1)]
    if (next) {
      event.preventDefault()
      next.focus()
    }
  }, [onNudge])

  const instantAt = (clientX) => fromMs + (clientX - trackRef.current.getBoundingClientRect().left) / pxPerMs

  const handlePointerDown = (event) => {
    dragging.current = event.currentTarget.getAttribute('data-handle')
    if (event.currentTarget.setPointerCapture && event.pointerId !== undefined) event.currentTarget.setPointerCapture(event.pointerId)
    event.preventDefault()
  }
  const handlePointerMove = (event) => {
    if (dragging.current) onDrag(dragging.current, instantAt(event.clientX))
  }
  const handlePointerUp = () => { dragging.current = '' }

  const workerRow = WORKER_ROWS[lane.kind]
  const className = `dc-row dc-lane${alternate ? ' dc-lane--alt' : ''}${workerRow ? ` dc-lane--${lane.kind}` : ''}${lane.kind === 'presence' ? ' dc-lane--worker-top' : ''}`
  const band = overlay && overlay.band
  return (
    <div className={className} data-lane={lane.key}>
      <div className='dc-cell-l'>
        {workerRow
          ? (
            <Tooltip title={t_(workerRow.hint)} placement='right' describeChild>
              <span className={`dc-label dc-label--${lane.kind}`}>
                {lane.kind === 'presence' && <b>{lane.label}</b>}
                <span className='dc-caption'><workerRow.Icon aria-hidden='true' />{t_(workerRow.caption)}</span>
              </span>
            </Tooltip>
            )
          : <span className='dc-label'><b>{lane.label}</b> {lane.sub}</span>}
      </div>
      <div ref={trackRef} className='dc-track' style={{ width: trackWidth, height: trackHeightOf(lane.segments) }} onFocus={handleFocus} onKeyDown={handleKeyDown}>
        {lane.notApplicable && <span className='dc-na'>{lane.notApplicable}</span>}
        {lane.segments.map((segment) => {
          const { left, width } = segmentBox({ startMs: segment.startMs, endMs: segment.endMs, fromMs, pxPerMs })
          const split = segment.subRows > 1
          const presence = lane.kind === 'presence'
          const height = presence ? PRESENCE_HEIGHT : split ? SUB_ROW_HEIGHT : SEGMENT_HEIGHT
          const top = presence ? PRESENCE_TOP : split ? SEGMENT_TOP + segment.subRow * (SUB_ROW_HEIGHT + SUB_ROW_GAP) : SEGMENT_TOP
          const interactive = segment.interventionIndex >= 0
          const classes = ['dc-seg', `dc-seg--${segment.tone}`]
          if (segment.open) classes.push('dc-seg--open')
          if (segment.mark !== 'none') classes.push('dc-seg--corr')
          if (segment.mark === 'filled') classes.push('dc-seg--fill')
          if (segment.suspicious) classes.push('dc-seg--sus')
          if (interactive) classes.push('dc-seg--clickable')
          if (highlightedRowIds.includes(segment.rowId)) classes.push('dc-seg--sel')
          const style = { left, width, top, height, lineHeight: `${height}px` }
          if (split) style.fontSize = '10.5px'
          else if (presence) style.fontSize = '10px'
          return (
            <div
              key={segment.key}
              className={classes.join(' ')}
              style={style}
              tabIndex={segment.key === tabbableKey ? 0 : -1}
              role={interactive ? 'button' : undefined}
              data-seg={`${blockKey}|${lane.key}|${segment.key}`}
              aria-label={`${lane.label} ${lane.sub}: ${segment.label}`}
            >
              {segment.badge && <span className='dc-badge'>{segment.badge}</span>}
              {segment.label}
            </div>
          )
        })}
        {band && Number.isFinite(band.startMs) && Number.isFinite(band.endMs) && band.endMs > band.startMs && (
          <span className='dc-ghost' data-testid='stretch-band' data-start={band.startMs} data-end={band.endMs} style={{ left: (band.startMs - fromMs) * pxPerMs, width: (band.endMs - band.startMs) * pxPerMs }} />
        )}
        {overlay && overlay.handles && overlay.handles.map((handle) => (
          <span
            key={handle.kind}
            className='dc-handle'
            role='slider'
            tabIndex={0}
            data-handle={handle.kind}
            aria-label={handle.kind === 'ADJUST_START' ? t_('Real start') : t_('Real end')}
            aria-valuenow={handle.valueMs}
            aria-valuetext={formatDateTime(handle.valueMs)}
            style={{ left: (handle.valueMs - fromMs) * pxPerMs }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          />
        ))}
      </div>
    </div>
  )
}

export default React.memo(TimelineLane)
